import React, { useCallback, useEffect, useState, useRef, Suspense } from 'react';
import { HeartIcon, XIcon, HeadphonesIcon, ThermometerIcon, MenuIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
// Sample book data
const sampleBooks = [{
  id: '1',
  title: 'The Silent Echo',
  author: 'Elena Richards',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=800',
  genre: 'Mystery Thriller',
  subgenre: 'Psychological',
  relationshipType: 'Enemies to Lovers',
  spiceLevel: 3,
  darknessLevel: 4,
  description: 'A gripping thriller that follows detective Sarah Miller as she investigates a series of mysterious disappearances in a small coastal town. As she delves deeper into the case, she uncovers secrets that were meant to stay buried.',
  price: 14.99,
  hasAudio: true,
  tags: ['Female Detective', 'Small Town', 'Suspense']
}, {
  id: '2',
  title: 'Heart of the Storm',
  author: 'Marcus Chen',
  coverImage: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=800',
  genre: 'Fantasy Romance',
  subgenre: 'Epic Fantasy',
  relationshipType: 'Forbidden Love',
  spiceLevel: 4,
  darknessLevel: 2,
  description: 'In a world where elemental magic determines social status, a young woman with extraordinary storm powers must navigate court intrigue and forbidden romance while discovering the true extent of her abilities.',
  price: 16.99,
  hasAudio: true,
  tags: ['Magic', 'Slow Burn', 'Political Intrigue']
}, {
  id: '3',
  title: 'Whispers in the Dark',
  author: 'Sarah Johnson',
  coverImage: 'https://images.unsplash.com/photo-1598646506899-ac6be1060f4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=800',
  genre: 'Horror',
  subgenre: 'Supernatural',
  relationshipType: 'None',
  spiceLevel: 1,
  darknessLevel: 5,
  description: 'A chilling tale of supernatural encounters in an abandoned Victorian mansion that has been shunned by locals for generations. When a group of friends decide to spend the night, they awaken something that should have remained dormant.',
  price: 12.99,
  hasAudio: false,
  tags: ['Haunted House', 'Paranormal', 'Gore']
}, {
  id: '4',
  title: 'Midnight Shadows',
  author: 'Sarah Johnson',
  coverImage: 'https://images.unsplash.com/photo-1515876305430-f06edab8282a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=800',
  genre: 'Supernatural Thriller',
  subgenre: 'Paranormal',
  relationshipType: 'Forced Proximity',
  spiceLevel: 2,
  darknessLevel: 4,
  description: "When strange shadows begin appearing in a small New England town, residents must confront long-buried secrets before it's too late. A detective with a troubled past teams up with a local historian to uncover the truth.",
  price: 13.99,
  hasAudio: true,
  tags: ['Small Town', 'Mystery', 'Paranormal']
}, {
  id: '5',
  title: 'Soul of Fire',
  author: 'Marcus Chen',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=800',
  genre: 'Fantasy Romance',
  subgenre: 'Epic Fantasy',
  relationshipType: 'Forbidden Love',
  spiceLevel: 4,
  darknessLevel: 3,
  description: 'As war looms on the horizon, our heroine must master the fire within her soul while protecting those she loves from mounting dangers. The second book in the Elements Trilogy raises the stakes with more action, romance, and political intrigue.',
  price: 16.99,
  hasAudio: true,
  tags: ['Magic', 'War', 'Slow Burn']
}];
// Filter interface
interface FilterOptions {
  genres: string[];
  tropes: string[];
  minSpiceLevel: number;
  maxSpiceLevel: number;
  audioOnly: boolean;
}
const BookDiscovery: React.FC = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // State
  const [currentIndex, setCurrentIndex] = useState(0);
  const [books, setBooks] = useState(sampleBooks);
  const [showFilters, setShowFilters] = useState(false);
  const [audioOnly, setAudioOnly] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    genres: [],
    tropes: [],
    minSpiceLevel: 0,
    maxSpiceLevel: 5,
    audioOnly: false
  });
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  // Derived state
  const deltaX = currentX - startX;
  const rotateDeg = deltaX * 0.05;
  const opacity = Math.min(Math.abs(deltaX) / 100, 1);
  const isLike = deltaX > 0;
  const isNope = deltaX < 0;
  // Filter logic
  useEffect(() => {
    let filteredBooks = [...sampleBooks];
    if (audioOnly) {
      filteredBooks = filteredBooks.filter(book => book.hasAudio);
    }
    if (filterOptions.genres.length > 0) {
      filteredBooks = filteredBooks.filter(book => filterOptions.genres.includes(book.genre) || filterOptions.genres.includes(book.subgenre));
    }
    if (filterOptions.tropes.length > 0) {
      filteredBooks = filteredBooks.filter(book => filterOptions.tropes.includes(book.relationshipType) || book.tags.some(tag => filterOptions.tropes.includes(tag)));
    }
    filteredBooks = filteredBooks.filter(book => book.spiceLevel >= filterOptions.minSpiceLevel && book.spiceLevel <= filterOptions.maxSpiceLevel);
    setBooks(filteredBooks);
    setCurrentIndex(0);
  }, [filterOptions, audioOnly]);
  // Swipe handlers
  const handleSwipeComplete = useCallback((direction: 'left' | 'right') => {
    setIsDragging(false);
    setStartX(0);
    setCurrentX(0);
    // Animate out
    if (cardRef.current) {
      const sign = direction === 'right' ? 1 : -1;
      cardRef.current.style.transition = 'transform 0.3s ease-out';
      cardRef.current.style.transform = `translateX(${sign * window.innerWidth}px) rotate(${sign * 30}deg)`;
    }
    // Advance index after animation
    setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % books.length);
      // Reset transform for next card (though React key change handles this mostly)
      if (cardRef.current) {
        cardRef.current.style.transition = 'none';
        cardRef.current.style.transform = 'none';
      }
    }, 300);
  }, [books.length]);
  // Mouse/Touch handlers
  const handleStart = (clientX: number) => {
    setIsDragging(true);
    setStartX(clientX);
    setCurrentX(clientX);
    if (cardRef.current) {
      cardRef.current.style.transition = 'none';
    }
  };
  const handleMove = (clientX: number) => {
    if (!isDragging) return;
    setCurrentX(clientX);
  };
  const handleEnd = () => {
    if (!isDragging) return;
    if (Math.abs(deltaX) > 100) {
      handleSwipeComplete(deltaX > 0 ? 'right' : 'left');
    } else {
      // Reset
      setIsDragging(false);
      setStartX(0);
      setCurrentX(0);
      if (cardRef.current) {
        cardRef.current.style.transition = 'transform 0.3s ease-out';
        cardRef.current.style.transform = 'none';
      }
    }
  };
  // Event Listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handleSwipeComplete('left');
      if (e.key === 'ArrowRight') handleSwipeComplete('right');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSwipeComplete]);
  // Helper to render tags
  const renderTag = (tag: string, type: 'genre' | 'trope' | 'relationship') => {
    let bgColor = dk.badgeNeutral;
    if (type === 'genre') {
      bgColor = dk.isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800';
    } else if (type === 'relationship') {
      bgColor = dk.isDark ? 'bg-pink-900/40 text-pink-300' : 'bg-pink-100 text-pink-800';
    } else if (type === 'trope') {
      bgColor = dk.isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-800';
    }
    return <span key={tag} className={`text-xs px-2 py-1 ${rt.badgeRadius} ${bgColor} mr-2 mb-2 inline-block`} data-id="element-8322" data-mp="glmhnoui28xc">
        {tag}
      </span>;
  };
  // Helper to render spice
  const renderSpiceLevel = (level: number) => <div className="flex items-center" data-id="element-8323" data-mp="bnbaxmzi4ejc">
      <span className={`text-xs mr-2 ${dk.textSecondary}`} data-id="element-8324" data-mp="boe1ftswweyq">Spice:</span>
      <div className="flex" data-id="element-8325" data-mp="212iwaffrlaj9">
        {Array.from({
        length: 5
      }).map((_, i) => <div key={i} className={`h-3 w-3 ${i < level ? 'text-red-500' : dk.textMuted}`} data-id="element-8326" data-mp="2613yuliho6av" data-mpi={i}>
            🔥
          </div>)}
      </div>
    </div>;
  // Helper to render darkness
  const renderDarknessLevel = (level: number) => <div className="flex items-center" data-id="element-8327" data-mp="3ligvxqfusi5s">
      <span className={`text-xs mr-2 ${dk.textSecondary}`} data-id="element-8328" data-mp="4420dqf9e9sp">Dark:</span>
      <div className="flex" data-id="element-8329" data-mp="3vihlhu4bfuif">
        {Array.from({
        length: 5
      }).map((_, i) => <ThermometerIcon key={i} className={`h-3 w-3 ${i < level ? 'text-indigo-500' : dk.textMuted}`} data-id="element-8330" data-mp="d48b54jkovo6" data-mpi={i} />)}
      </div>
    </div>;
  // Current and ghost books
  const activeBook = books[currentIndex];
  const ghost1 = books[(currentIndex + 1) % books.length];
  const ghost2 = books[(currentIndex + 2) % books.length];
  if (!activeBook) return <div className="p-8 text-center" data-id="element-8331" data-mp="2ou194244omwd">No books found matching filters.</div>;
  return <div className={`min-h-screen flex flex-col ${dk.page} overflow-hidden`} data-id="element-8332" data-mp="2ovvqj6g6xwvp">
      {/* ─── Top Bar ────────────────────────────────────────────────────── */}
      <header className={`h-16 flex items-center justify-between px-4 border-b ${dk.border} ${dk.surface} z-20`} data-id="element-8333" data-mp="3oo6x8g6kglrb">
        <div className="flex items-center gap-3" data-id="element-8334" data-mp="3jpvn04cd098k">
          <button onClick={() => setShowFilters(true)} className={`p-2 rounded-full ${dk.textSecondary}`} data-id="element-8335" data-mp="3ephdsiz0ut8y">
            <MenuIcon className="h-6 w-6" data-id="element-8336" data-mp="39rfxu6ww7a1p" />
          </button>
          <h1 className={`text-lg font-bold ${dk.textHeading} hidden sm:block`} data-id="element-8337" data-mp="b6ebelwxq3ao">
            Discovery
          </h1>
        </div>
        <span className={`text-sm font-medium ${dk.textMuted}`} data-id="element-8338" data-mp="6apk15v2jspg">
          {currentIndex + 1} / {books.length}
        </span>
      </header>

      {/* ─── Main Arena ─────────────────────────────────────────────────── */}
      <main className="flex-grow relative flex flex-col items-center justify-center p-4 overflow-hidden" data-id="element-8339" data-mp="ty75ykrnjwki">
        {/* Desktop: card flanked by action buttons; Mobile: card only */}
        <div className="flex items-center gap-6 md:gap-10" data-id="element-8340" data-mp="p5e362fquyb2">
          {/* Skip — desktop only, left of card */}
          <button onClick={() => handleSwipeComplete('left')} className={`hidden md:flex p-5 rounded-full shadow-xl transition transform hover:scale-110 active:scale-95 ${dk.card} border ${dk.border} text-red-500`} aria-label="Skip" data-id="element-8341" data-mp="11hq6w60uqahd">
            <XIcon className="h-9 w-9" data-id="element-8342" data-mp="1bdt7v8jyw32q" />
          </button>

          {/* Card Stack */}
          <div className="relative w-72 md:w-80 flex-shrink-0" style={{
          aspectRatio: '2/3'
        }} data-id="element-8343" data-mp="1geqohgpbwp24">
            {/* Ghost Card 2 (Back) */}
            {ghost2 && <div className={`absolute inset-0 rounded-2xl shadow-xl overflow-hidden ${dk.card}`} style={{
            transform: 'scale(0.9) translateY(30px)',
            opacity: 0.3,
            zIndex: 0
          }} data-id="element-8344" data-mp="1u6dwgdsmvm0q">
                <div className="w-full h-full bg-cover bg-center" style={{
              backgroundImage: `url(${ghost2.coverImage})`
            }} data-id="element-8345" data-mp="24279dya9lhj1" />
              </div>}

            {/* Ghost Card 1 (Middle) */}
            {ghost1 && <div className={`absolute inset-0 rounded-2xl shadow-xl overflow-hidden ${dk.card}`} style={{
            transform: 'scale(0.95) translateY(15px)',
            opacity: 0.6,
            zIndex: 1
          }} data-id="element-8346" data-mp="1jfsei252ayj3">
                <div className="w-full h-full bg-cover bg-center" style={{
              backgroundImage: `url(${ghost1.coverImage})`
            }} data-id="element-8347" data-mp="274ti9c2y9li1" />
              </div>}

            {/* Active Card (Front) — full bleed cover image */}
            <div ref={cardRef} className="absolute inset-0 rounded-2xl shadow-2xl overflow-hidden cursor-grab active:cursor-grabbing" style={{
            zIndex: 10,
            transform: isDragging ? `translateX(${deltaX}px) rotate(${rotateDeg}deg)` : 'none'
          }} onMouseDown={e => handleStart(e.clientX)} onMouseMove={e => handleMove(e.clientX)} onMouseUp={handleEnd} onMouseLeave={handleEnd} onTouchStart={e => handleStart(e.touches[0].clientX)} onTouchMove={e => handleMove(e.touches[0].clientX)} onTouchEnd={handleEnd} data-id="element-8348" data-mp="7sr3gf59tfap">
              {/* Full-bleed cover image */}
              <div className="w-full h-full bg-cover bg-center relative group" style={{
              backgroundImage: `url(${activeBook.coverImage})`
            }} data-id="element-8349" data-mp="1vp8eg0rn4rt1">
                {/* Audio Badge */}
                {activeBook.hasAudio && <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md text-white p-2 rounded-full" data-id="element-8350" data-mp="1q21klk7v6v0c">
                    <HeadphonesIcon className="h-4 w-4" data-id="element-8351" data-mp="1l3i1rsokhzc7" />
                  </div>}

                {/* LIKE stamp */}
                <div className="absolute top-8 left-8 border-4 border-green-500 text-green-500 font-black text-4xl px-4 py-2 rounded-lg transform -rotate-12 pointer-events-none select-none" style={{
                opacity: isLike ? opacity : 0
              }} data-id="element-8352" data-mp="1h2y50q7woxql">
                  LIKE
                </div>
                {/* NOPE stamp */}
                <div className="absolute top-8 right-8 border-4 border-red-500 text-red-500 font-black text-4xl px-4 py-2 rounded-lg transform rotate-12 pointer-events-none select-none" style={{
                opacity: isNope ? opacity : 0
              }} data-id="element-8353" data-mp="xarnhct6z3yr">
                  NOPE
                </div>

                {/* Bottom gradient — trope pill only */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pt-24 pb-5 px-5" data-id="element-8354" data-mp="2zz9joid29zki">
                  {activeBook.relationshipType && activeBook.relationshipType !== 'None' && <span className={`inline-block px-3 py-1 ${rt.badgeRadius} text-xs font-bold bg-pink-600 text-white shadow-sm`} data-id="element-8355" data-mp="38zeqorecevy5">
                        {activeBook.relationshipType}
                      </span>}
                </div>

                {/* Hover description overlay (desktop) */}
                <div className="absolute inset-0 bg-black/80 backdrop-blur-sm p-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden md:flex flex-col justify-center items-center text-center pointer-events-none" data-id="element-8356" data-mp="2q1lppuna4w3e">
                  <p className="text-gray-100 leading-relaxed text-sm md:text-base mb-4" data-id="element-8357" data-mp="1xj7zag49wcec">
                    {activeBook.description}
                  </p>
                  <p className="text-gray-400 text-xs" data-id="element-8358" data-mp="2vxn9pqhvn031">
                    ← skip &nbsp;&nbsp; save →
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Save — desktop only, right of card */}
          <button onClick={() => handleSwipeComplete('right')} className={`hidden md:flex p-5 rounded-full shadow-xl transition transform hover:scale-110 active:scale-95 ${dk.card} border ${dk.border} text-green-500`} aria-label="Save" data-id="element-8359" data-mp="35orlyig646u6">
            <HeartIcon className="h-9 w-9" data-id="element-8360" data-mp="2qz3lfjft9c7i" />
          </button>
        </div>

        {/* Action Buttons — mobile only, below card */}
        <div className="mt-8 flex md:hidden items-center gap-8 z-20" data-id="element-8361" data-mp="67jjblgd5m7k">
          <button onClick={() => handleSwipeComplete('left')} className={`p-4 rounded-full shadow-lg transition transform active:scale-95 ${dk.card} border ${dk.border} text-red-500`} aria-label="Skip" data-id="element-8362" data-mp="65qfedx4xg39">
            <XIcon className="h-8 w-8" data-id="element-8363" data-mp="ep3hvn1h2nzc" />
          </button>
          <button onClick={() => handleSwipeComplete('right')} className={`p-4 rounded-full shadow-lg transition transform active:scale-95 ${dk.card} border ${dk.border} text-green-500`} aria-label="Save" data-id="element-8364" data-mp="emhb3pmeoci6">
            <HeartIcon className="h-8 w-8" data-id="element-8365" data-mp="sh05bds749pe" />
          </button>
        </div>
      </main>

      {/* ─── Filter Panel (Slide-in) ────────────────────────────────────── */}
      {showFilters && <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setShowFilters(false)} data-id="element-8366" data-mp="ipdxzsizaurx" />
          <div className={`fixed inset-y-0 left-0 w-80 shadow-2xl z-50 ${dk.surface} border-r ${dk.border}`} data-id="element-8367" data-mp="1bet90ian8nnu">
            <div className={`p-4 border-b ${dk.border} flex justify-between items-center`} data-id="element-8368" data-mp="1g5t73qvi1brx">
              <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-8369" data-mp="3ca8x8rx769m7">Filters</h2>
              <button onClick={() => setShowFilters(false)} className={`p-2 rounded-full ${dk.textMuted}`} data-id="element-8370" data-mp="37gwm9klfy1bx">
                <XIcon className="h-5 w-5" data-id="element-8371" data-mp="33e00xm4inr4a" />
              </button>
            </div>

            <div className="p-6 space-y-8 overflow-y-auto h-[calc(100%-4rem)]" data-id="element-8372" data-mp="2yf6kbktxnjzw">
              {/* Audio Only */}
              <div className="flex items-center justify-between" data-id="element-8373" data-mp="3v57drx58yonj">
                <div data-id="element-8374" data-mp="3r6gfvnsyj2zd">
                  <h3 className={`font-medium ${dk.textHeading}`} data-id="element-8375" data-mp="3r440niel36q0">
                    Audio Only
                  </h3>
                  <p className={`text-xs mt-0.5 ${dk.textMuted}`} data-id="element-8376" data-mp="3h80zaerk3c44">
                    Show audiobooks only
                  </p>
                </div>
                <button onClick={() => setAudioOnly(!audioOnly)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${audioOnly ? rt.bgDark : dk.isDark ? 'bg-gray-600' : 'bg-gray-200'}`} data-id="element-8377" data-mp="2at9rrs7xgt14">
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${audioOnly ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-8378" data-mp="20otff8tnpu22" />
                </button>
              </div>

              {/* Genres */}
              <div data-id="element-8379" data-mp="2efoha15qnaw5">
                <h3 className={`font-medium mb-3 ${dk.textHeading}`} data-id="element-8380" data-mp="2ee510ak30zud">Genres</h3>
                <div className="flex flex-wrap gap-2" data-id="element-8381" data-mp="2jdq6z4xo68gl">
                  {[...new Set(sampleBooks.map(b => b.genre))].map(genre => <button key={genre} onClick={() => {
                const newGenres = filterOptions.genres.includes(genre) ? filterOptions.genres.filter(g => g !== genre) : [...filterOptions.genres, genre];
                setFilterOptions({
                  ...filterOptions,
                  genres: newGenres
                });
              }} className={`px-3 py-1.5 ${rt.badgeRadius} text-xs transition-colors ${filterOptions.genres.includes(genre) ? `${rt.bgDark} text-white` : `${dk.badgeNeutral}`}`} data-id="element-8382" data-mp="2obi1m6pp88oh">
                        {genre}
                      </button>)}
                </div>
              </div>

              {/* Tropes & Relationships */}
              <div data-id="element-8383" data-mp="3utvur9cnu2m7">
                <h3 className={`font-medium mb-3 ${dk.textHeading}`} data-id="element-8384" data-mp="3ute18khdu2hz">
                  Tropes & Relationships
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-8385" data-mp="3mt4ql8k62dk">
                  {[...new Set([...sampleBooks.map(b => b.relationshipType).filter(r => r !== 'None'), ...sampleBooks.flatMap(b => b.tags)])].map(trope => <button key={trope} onClick={() => {
                const newTropes = filterOptions.tropes.includes(trope) ? filterOptions.tropes.filter(t => t !== trope) : [...filterOptions.tropes, trope];
                setFilterOptions({
                  ...filterOptions,
                  tropes: newTropes
                });
              }} className={`px-3 py-1.5 ${rt.badgeRadius} text-xs transition-colors ${filterOptions.tropes.includes(trope) ? `${rt.bgDark} text-white` : `${dk.badgeNeutral}`}`} data-id="element-8386" data-mp="1t3ole5hx7y00">
                      {trope}
                    </button>)}
                </div>
              </div>

              {/* Spice Level */}
              <div data-id="element-8387" data-mp="swjithj20pix">
                <h3 className={`font-medium mb-1 ${dk.textHeading}`} data-id="element-8388" data-mp="sw0cyz6ohrl9">
                  Spice Level
                </h3>
                <div className={`flex justify-between text-xs mb-3 ${dk.textMuted}`} data-id="element-8389" data-mp="xu1w1k43tqwq">
                  <span data-id="element-8390" data-mp="ixu766hr41b4">
                    {'🔥'.repeat(filterOptions.minSpiceLevel) || 'None'}
                  </span>
                  <span data-id="element-8391" data-mp="a5sckoud5f8r">{'🔥'.repeat(filterOptions.maxSpiceLevel)}</span>
                </div>
                <div className="space-y-3" data-id="element-8392" data-mp="a3xtlg2njx7b">
                  <div data-id="element-8393" data-mp="exk4b2p99r0h">
                    <label className={`text-xs ${dk.textMuted} block mb-1`} data-id="element-8394" data-mp="extkxzoeulti">
                      Min
                    </label>
                    <input type="range" min="0" max="5" value={filterOptions.minSpiceLevel} onChange={e => setFilterOptions({
                  ...filterOptions,
                  minSpiceLevel: parseInt(e.target.value)
                })} className="w-full accent-pink-500" data-id="element-8395" data-mp="56p753ah1709" />
                  </div>
                  <div data-id="element-8396" data-mp="3hqtb6byhj51k">
                    <label className={`text-xs ${dk.textMuted} block mb-1`} data-id="element-8397" data-mp="3hr2ytsw25043">
                      Max
                    </label>
                    <input type="range" min="0" max="5" value={filterOptions.maxSpiceLevel} onChange={e => setFilterOptions({
                  ...filterOptions,
                  maxSpiceLevel: parseInt(e.target.value)
                })} className="w-full accent-pink-500" data-id="element-8398" data-mp="3ctbaf8umfw4n" />
                  </div>
                </div>
              </div>

              {/* Darkness Level */}
              <div data-id="element-8399" data-mp="98nbjgmntd5v">
                <h3 className={`font-medium mb-1 ${dk.textHeading}`} data-id="element-8400" data-mp="tcul49gvl58p">
                  Darkness Level
                </h3>
                <div className={`flex justify-between text-xs mb-3 ${dk.textMuted}`} data-id="element-8401" data-mp="xdemjp8hev0n">
                  <span data-id="element-8402" data-mp="125g49qof5dy2">Min: {filterOptions.minSpiceLevel}</span>
                  <span data-id="element-8403" data-mp="174j0qmqc2buf">Max: {filterOptions.maxSpiceLevel}</span>
                </div>
                <div className="space-y-3" data-id="element-8404" data-mp="ae08b60z2ev6">
                  <div data-id="element-8405" data-mp="afbax2iolgkp">
                    <label className={`text-xs ${dk.textMuted} block mb-1`} data-id="element-8406" data-mp="aftigvasnh7x">
                      Min
                    </label>
                    <input type="range" min="0" max="5" value={filterOptions.minSpiceLevel} onChange={e => setFilterOptions({
                  ...filterOptions,
                  minSpiceLevel: parseInt(e.target.value)
                })} className="w-full accent-indigo-500" data-id="element-8407" data-mp="jeooo7du8rb8" />
                  </div>
                  <div data-id="element-8408" data-mp="3fcnusrcl7gdq">
                    <label className={`text-xs ${dk.textMuted} block mb-1`} data-id="element-8409" data-mp="3fceb1li4y5gh">
                      Max
                    </label>
                    <input type="range" min="0" max="5" value={filterOptions.maxSpiceLevel} onChange={e => setFilterOptions({
                  ...filterOptions,
                  maxSpiceLevel: parseInt(e.target.value)
                })} className="w-full accent-indigo-500" data-id="element-8410" data-mp="35lt3v9oebsly" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
};
export default BookDiscovery;